import React, { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
// @ts-ignore
import permissions from 'invitemember-permissions-model';
import StaticLayoutInner from '@/layouts/StaticLayoutInner';
import PlusIcon from '@/images/icons/Plus';
import TableLayout from '@/layouts/TableLayout';
import BaseDynamicAPITable from '@/components/Tables/BaseDynamicAPITable';
import NewPlanModal from '@/pages/settings/SubscriptionPlans/NewPlanModal';
import { OutletContext, PlanType } from '@/types';
import { humanizeDuration } from '@/utils/durations';
import { fetchAPI } from '@/api';
import { useHasPermission } from '@/hooks/useHasPermission';
import ErrorModal from '@/components/ErrorModal';
import EyeCrossedCircle from '@/images/icons/EyeCrossedCircle';
import { useWindowWidth } from '@/hooks/useWindowWidth';
import { priceView } from '@/utils/utils';

function SubscriptionPlans() {
	const { auth, project } = useOutletContext<OutletContext>();
	const hasCreatePermissions = useHasPermission(permissions.CREATE_SUBSCRIPTION_PLANS);
	const hasPlanPermissions = useHasPermission(permissions.MENU_SUBSCRIPTION_PLAN);

	const [newPlanModalOpen, setNewPlanModalOpen] = useState(false);
	const [isNewPlanLoading, setIsNewPlanLoading] = useState(false);
	const [errorModal, setErrorModal] = useState(null);
	const [errorModalOpen, setErrorModalOpen] = useState(false);

	const button = (
		hasCreatePermissions && (
			<button className="btn-secondary" onClick={() => setNewPlanModalOpen(true)}>
				Add new plan
				<PlusIcon className="w-5 h-5 ml-1 -mr-1" />
			</button>
		)
	);

	const onActivatePlan = async (planId: string) => {
		try {
			const response = await fetchAPI("/plan/update", auth.user.token, {
				projectId: project.projectId,
				planId: planId,
				isHidden: false,
			});
			const result = await response.json();

			if (response.ok && result.plan) {
				window.location.reload()
			} else {
				throw new Error(result.message || "Failed to update the project.");
			}
		} catch (error: any) {
			console.error("Error updating project:", error.message);
			throw error;
		}
	}

	const onPlanCreate = async (plan: Partial<PlanType>) => {
		setIsNewPlanLoading(true);
		try {
			const response = await fetchAPI("/plan/create", auth.user.token, {
				projectId: project.projectId,
				...plan,
			});
			const result = await response.json();

			if (result.plan) {
				setNewPlanModalOpen(false);
				setTimeout(() => {
					window.location.reload();
				}, 500);
			}
		} catch (error: any) {
			console.log("Error updating project:", error.message);
			setErrorModal(error.message || "Failed to create a plan");
		} finally {
			setIsNewPlanLoading(false);
		}
	};

	useEffect(() => {
		if (errorModal) {
			setNewPlanModalOpen(false);
			setErrorModalOpen(true);
		}
	}, [errorModal]);

	useEffect(() => {
		if (!errorModalOpen) {
			setErrorModal(null);
		}
	}, [errorModalOpen]);

	return (
		<StaticLayoutInner>
			<TableLayout
				title="Subscription plans"
				description="Here are project subscription plans."
				button={button}
			>
				<BaseDynamicAPITable
					apiMethod="/project/plans"
					pageSize={15}
					dataModel={{
						columns: [
							{
								header: "Name",
								align: "left",
								width: useWindowWidth() > 1600 ? 25 : 35,
								render: (row: any) => (
									<div className="flex gap-2.5">
										<span className="font-semibold mt-[3px]">{row.name}</span>
										{row.isHidden && <EyeCrossedCircle className="w-6 h-6" backgroundColor="#F2F2F2" />}
									</div>
								),
								hideHeaderMobile: true,
								fullWidthMobile: true,
							}, {
								header: "Price",
								align: "right",
								width: useWindowWidth() > 1600 ? 8 : 10,
								hideHeaderMobile: true,
								removePaddingRight: true,
                                                                render: (row: any) => <span className="font-semibold">{row.price?.amount === 0 ? '0' : priceView(row.price?.amount, row.price?.currency)}</span>,
								hideMobile: true,
							}, {
								header: "",
								align: "left",
								width: 10,
								hideHeaderMobile: true,
								hideMobile: true,
                                                                render: (row: any) => (
                                                                        <span className="text-[#A5A5A5] font-semibold">{row.price?.amount === 0 ? 'FREE' : (row.price?.currency === 'XTR' ? '⭐' : row.price?.currency)}</span>
                                                                ),
							}, {
								header: "Price",
								align: "left",
								width: 10,
								fullWidthMobile: true,
								hideDesktop: true,
                                                                render: (row: any) => (
                                                                        <div className="">
                                                                                <span className="font-semibold">{row.price?.amount === 0 ? '0' : priceView(row.price?.amount, row.price?.currency)}</span>
                                                                                <span className="text-[#A5A5A5] font-semibold ml-2">{row.price?.amount === 0 ? 'FREE' : (row.price?.currency === 'XTR' ? '⭐' : row.price.currency)}</span>
                                                                        </div>
                                                                ),
							}, {
								header: "Period",
								align: "left",
								fullWidthMobile: true,
								render: (row: any) => (
									<>
										<span>{humanizeDuration(row.period || 0)}</span>
									</>
								),
							},
						]
					}}
					rowLinkTo={(row: any) => (hasPlanPermissions ? `/settings/subscription-plans/${row.planId}` : '')}
					rowLinkState={(row: any) => row}
				/>
			</TableLayout>
			<NewPlanModal
				modalOpen={newPlanModalOpen}
				setModalOpen={setNewPlanModalOpen}
				submitCallback={onPlanCreate}
			/>
			<ErrorModal
				modalOpen={errorModalOpen}
				setModalOpen={setErrorModalOpen}
				error={errorModal}
			/>
		</StaticLayoutInner>
	);
}

export default SubscriptionPlans;
